import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { ListShell } from "@/components/shared/ListShell";
import { ListToolbar } from "@/components/shared/ListToolbar";
import { DataTable, type DataTableColumn } from "@/components/shared/DataTable";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import { ScreenGuard } from "@/components/rbac/ScreenGuard";
import { useDeleteRole, useRoles } from "@/hooks/useRbac";
import type { RoleSummary } from "@/rbac/types";

export const RolesList = () => {
  const navigate = useNavigate();
  const { data: roles = [], isLoading, error, refetch } = useRoles();
  const deleteMut = useDeleteRole();

  const handleDelete = (r: RoleSummary) => {
    if (r.memberCount > 0) {
      toast.error(
        `Cannot delete role with ${r.memberCount} active member(s). Reassign them first.`,
      );
      return;
    }
    if (!window.confirm(`Delete role "${r.name}"? This cannot be undone.`))
      return;
    deleteMut.mutate(r.id, {
      onSuccess: () => toast.success("Role deleted"),
      onError: (e) => toast.error(e.message),
    });
  };

  const columns: Array<DataTableColumn<RoleSummary>> = [
    {
      key: "name",
      header: "Role",
      render: (r) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{r.name}</span>
          {r.isSystemRole && (
            <span
              title="System role — permissions editable, name locked"
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
            >
              <Lock className="h-3 w-3" /> System
            </span>
          )}
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (r) => (
        <span className="text-muted-foreground text-sm">
          {r.description ?? "—"}
        </span>
      ),
    },
    {
      key: "members",
      header: "Members",
      render: (r) => <span className="tabular-nums">{r.memberCount}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <PermissionGate module="staffManagement" action="edit">
            <button
              type="button"
              onClick={() => navigate(`/roles/${r.id}`)}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground"
              aria-label="Edit role"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </PermissionGate>
          {!r.isSystemRole && (
            <PermissionGate module="staffManagement" action="delete">
              <button
                type="button"
                onClick={() => handleDelete(r)}
                className="p-1.5 rounded hover:bg-destructive/10 text-destructive"
                aria-label="Delete role"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </PermissionGate>
          )}
        </div>
      ),
    },
  ];

  return (
    <ScreenGuard module="staffManagement">
      <AppLayout title="Roles">
        <ListToolbar
          actions={
            <PermissionGate module="staffManagement" action="create">
              <button
                type="button"
                onClick={() => navigate("/roles/new")}
                className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium rounded-btn px-4 py-2 hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" /> New role
              </button>
            </PermissionGate>
          }
        />
        <ListShell
          isLoading={isLoading}
          error={error ? (error as Error).message : null}
          isEmpty={roles.length === 0}
          onRetry={refetch}
          emptyTitle="No roles"
          emptyDescription="Create roles to group permissions."
        >
          <DataTable columns={columns} rows={roles} rowKey={(r) => r.id} />
        </ListShell>
      </AppLayout>
    </ScreenGuard>
  );
};

export default RolesList;
